import React, { useEffect, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import preval from 'preval.macro';
import { FooterListComp } from './FooterListComp';
import Accordian from 'atoms/Accordian/Accordian';
import { AnchorWithLink } from 'atoms/Anchor';
import { modifyData, copyLinkList } from '../FooterUtilities/FooterUtils';
import { FooterAppButtonsComp } from './FooterAppButtons';
import { getTernaryValue, isSupportedTenantCode } from 'common/utilities/utils';
import { getImageComponent } from 'common/sharedTemplates/sharedTemplates';
import { UserOrderContext } from 'context/context';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import ListItems from './ListItems';
import { LanguageSelectorComp } from 'organisms/LanguageSelector/LanguageSelectorComp';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import STYLE_SCRIPT_CONST from 'common/style-scripts/constants';
import useStyleScripts from 'common/custom-hooks/useStyleScripts';
import { TooltipView } from 'atoms/Tooltip/index';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';

export const getFooterClassName = (localized, isPdpPageActive) => {
  return `site-footer${localized ? ' localized' : ''}${isPdpPageActive ? ' pdp' : ''}`;
};

/**
 * Main Footer Items Component
 * @param {*} props - Object - title
 */
const FooterItems = props => {
  const { contentData = [], clickHandler = null, tenantReducerData, navigateHandler = null } = props;
  const { bodyContent = {}, footerContent = {} } = modifyData(contentData) || {};
  const location = useLocation();
  const userParams = useSelector(data => data.appStateReducer.userStatus);
  const { storesWithStatus } = useSelector(data => data?.storesReducer) || {};
  const { categories = [], kfcLogo = {}, kfcStoreSearch = {}, downloadAppButtons = {} } = bodyContent;
  let { copyrightContentData = {}, socialMediaContent = {}, copyrightNavLinks = [] } = footerContent;
  const { userOrderState } = useContext(UserOrderContext);
  const { isPdpPageActive = false } = userOrderState;
  const [activeMenu, setActiveMenu] = useState();
  const CustomStyledFooter = useStyleScripts(STYLE_SCRIPT_CONST?.FOOTER_STYLES);
  const { KFCbucket } = IMAGE_PATH;
  const onAccordianclickHandler = (item, ind) => {
    setActiveMenu(prevActiveMenu => {
      const selectedMenu = `${item?.categoryHeaderText}_${ind}`;
      return prevActiveMenu === selectedMenu ? undefined : selectedMenu;
    });
  };

  useEffect(() => {
    setActiveMenu(false);
  }, [location?.pathname]);

  /*  draw category items */
  const categoryItems = () => {
    return categories?.map((item, ind) => {
      return <FooterListComp key={ind} className='footer-links' onClickHandler={clickHandler} {...item} />;
    });
  };

  const categoryItemsMobile = () => {
    return categories?.map((item, ind) => {
      return (
        <Accordian
          key={`${item?.categoryHeaderText}_${ind}`}
          header={item?.categoryHeaderText}
          isDarkMode={true}
          onClickHandler={() => onAccordianclickHandler(item, ind)}
          footerActiveMenu={`${item?.categoryHeaderText}_${ind}` === activeMenu}
          cssClass={getTernaryValue(`${item?.categoryHeaderText}_${ind}` === activeMenu, 'footer-accordian-open', '')}
        >
          <ul className='footer-mobile-list' tabIndex='0'>
            {item?.subCategories?.map((subItem, index) => {
              return (
                <>
                  <ListItems key={index} data-testid='listItems-component' onClickHandler={clickHandler} {...subItem}>
                    {subItem?.name}
                    {subItem?.externalurl && (
                      <img
                        src={subItem?.icon?.mobileImage}
                        className={subItem?.icon?.mobileImage ? 'footerIcon' : 'hideFooterIcon'}
                        alt='img'
                      />
                    )}
                  </ListItems>
                </>
              );
            })}
          </ul>
        </Accordian>
      );
    });
  };

  /*  draw social media items */
  const socialMediaItems = () => {
    return socialMediaContent?.map((item, ind) => {
      return (
        <FooterListComp
          key={ind}
          className='social-icons'
          data-testid='footerItemSocial-component'
          onClickHandler={clickHandler}
          {...item}
        />
      );
    });
  };

  // language dropdown element rendering here
  const langDropDownElmnt = () => {
    return (
      <div
        className={`${!isSupportedTenantCode() ? 'column lang-div' : 'lang-div-aus'} ${
          storesWithStatus?.length ? 'disposition-set' : ''
        }`}
      >
        {<LanguageSelectorComp hamburgerMenu={false} />}
      </div>
    );
  };

  return (
    CustomStyledFooter && (
      <CustomStyledFooter
        paddingBottom={document?.getElementById?.('start-order-component')?.clientHeight}
        className={getFooterClassName(userParams?.localized, isPdpPageActive)}
        data-testid='footer-component'
      >
        <div className='grid-container'>
          <div className='row row-one row-oneaus'>
            <div className='header-logo-footer mobile-header-logo'>
              <AnchorWithLink
                className='header-logo'
                data-testid='footer-component-navigate-handler'
                onClickHandler={navigateHandler}
              >
                {getImageComponent(
                  kfcLogo?.src || KFCbucket,
                  'FOOTER_KFC_BUCKET',
                  'kfc-footer-logo',
                  kfcLogo?.alt || 'KFC LOGO',
                )}
              </AnchorWithLink>
            </div>
            <div className='category-items'>{categoryItems()}</div>
            <div className='category-items row-mobile'>{categoryItemsMobile()}</div>
            <div className='findstore'>
              {getImageComponent(kfcStoreSearch?.url, 'FOOTER_FIND_STORE', '', 'Find Store')}
              <AnchorWithLink
                data-testid='footer-component-findKFC-handler'
                // href={kfcStoreSearch?.linkTo}
                onClickHandler={clickHandler}
                name={kfcStoreSearch?.name}
                titleText={kfcStoreSearch?.title}
                // targetWindow='_blank'
                tabIndex='0'
                // type={'external'}
              >
                {translateWithI18Next(kfcStoreSearch?.name)}
              </AnchorWithLink>
            </div>
            <hr className='mobile-horizontal'></hr>
            <div className='download-app-mobile'>
              <div className='download-app'>{translateWithI18Next(TRANSLATE_MAPPING_KEY.DOWNLOAD_APP)}</div>
              <div
                className={!isSupportedTenantCode() ? 'flex social-btns' : 'social-btns-aus'}
                data-testid='social-btns'
              >
                <FooterAppButtonsComp buttonContent={downloadAppButtons} />
              </div>
            </div>
          </div>

          <div className='row row-two row-twoaus'>
            {langDropDownElmnt()}
            <div
              className={`column copyright-mobile ${userParams?.localized ? 'localized' : 'non-localized'}`}
              data-testid='copyright-text-column'
            >
              <div className='footer-copyLink'>
                {tenantReducerData?.showFootercopyrightNavLinks && copyrightNavLinks?.length && (
                  <ul className='footer-linkview'>{copyLinkList(copyrightNavLinks)}</ul>
                )}
                <TooltipView placement='top' title={preval`module.exports = new Date().toLocaleString();`}>
                  <p className='copyright-text'>
                    {copyrightContentData?.name} {translateWithI18Next(TRANSLATE_MAPPING_KEY.BUILD)}{' '}
                    {process.env.REACT_APP_CI_PIPELINE_ID}
                  </p>
                </TooltipView>
              </div>
            </div>

            <div className='column social-div social-div-mobile'>
              <ul className='social-icons'>{socialMediaItems()}</ul>
            </div>
          </div>
        </div>
      </CustomStyledFooter>
    )
  );
};

export { FooterItems };
